// src/pages/ReportPage.js
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTeamContext } from "../TeamContext";
import TopBar from "../ui/TopBar";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import "../styles.css";
import placeholderArt from "../assets/placeholder_art.png";

ChartJS.register(ArcElement, Tooltip, Legend);

function ReportPage() {
    const navigate = useNavigate();
    const { activeWorkspaceId, getWorkspaceById, calculateFinalEquity, updateWorkspaceData } = useTeamContext();
    const ws = getWorkspaceById(activeWorkspaceId);
    const [showHelpPanel, setShowHelpPanel] = useState(false);
    const reportRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);
    const [aiReport, setAiReport] = useState("");
    const [isGenerating, setIsGenerating] = useState(false);

    const {
        finalEquityMap,
        reservedEquityTotal,
        coreBreakdown,
        factorAllocations
    } = calculateFinalEquity();

    // Replace the totalFinalEquity calculation with:
    const totalFounderEquity = (ws.part1Answers?.founderNames || []).reduce((sum, founder) => {
        const userValue = ws.part2Answers?.finalDecidedEquity?.[founder];
        const calculatedValue = finalEquityMap[founder] || 0;
        return sum + (typeof userValue === 'number' ? userValue : calculatedValue);
    }, 0);

    const totalAllocated = totalFounderEquity + reservedEquityTotal;

    const [rawInputs, setRawInputs] = useState({});

    const exportToPDF = async () => {
        setIsExporting(true);
        try {
            const input = reportRef.current;
            if (!input) throw new Error("Report content not found");

            const originalOverflow = input.style.overflow;
            input.style.overflow = "visible";

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;

            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                onclone: (clonedDoc) => {
                    const hp = clonedDoc.getElementById('help-panel');
                    if (hp) hp.style.display = 'none';
                }
            });

            const imgData = canvas.toDataURL('image/png');
            const imgProps = pdf.getImageProperties(imgData);
            const imgWidth = contentWidth;
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

            let pageCount = Math.ceil(imgHeight / pageHeight);

            pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
            for (let i = 1; i < pageCount; i++) {
                pdf.addPage();
                pdf.addImage(imgData, 'PNG',
                    margin,
                    margin - (pageHeight * i),
                    imgWidth,
                    imgHeight
                );
            }

            const companyName = ws.part1Answers.companyName || ws.name;
            pdf.save(`${companyName}_Equity_Report.pdf`);
            input.style.overflow = originalOverflow;
        } catch (error) {
            console.error('PDF Export Error:', error);
            alert(`Export failed: ${error.message}`);
        }
        setIsExporting(false);
    };

    if (!ws?.isFullQuestionnaireComplete) {
        return (
            <div className="wrapper">
                <div style={{ position: "relative", textAlign: "center", marginTop: "32px", color: "#28435a" }}>
                    <img
                        src={placeholderArt}
                        alt="placeholder"
                        style={{ width: "100%", maxWidth: "800px", marginLeft: "-100px" }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            top: "40%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "rgba(255,255,255,0.8)",
                            padding: "16px",
                            borderRadius: "8px",
                            maxWidth: "80%",
                            color: "#28435a"
                        }}
                    >
                        <h2 style={{ marginBottom: "8px" }}>
                            The secret of getting ahead is getting started
                        </h2>
                        <p style={{ marginBottom: "16px" }}>
                            To calculate your equity split, get started with the questionnaires below.
                        </p>
                        <button
                            onClick={() => navigate("/calculator")}
                            style={{
                                padding: "10px 20px",
                                backgroundColor: "#f7931e",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "16px",
                            }}
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const companyName = ws.part1Answers.companyName || ws.name;
    const reservedPools = ws.part1Answers.reservedPools || [];
    const founderEntries = Object.entries(finalEquityMap || {});

    // Revert the original color arrays for the chart slices:
    const createChartData = (founderEntries, reservedPools) => ({
        labels: [
            ...founderEntries.map(([fn]) => fn),
            ...reservedPools.map(rp => rp.name)
        ],
        datasets: [{
            data: [
                ...founderEntries.map(([_, eq]) => eq),
                ...reservedPools.map(rp => rp.weight)
            ],
            // ORIGINAL color array from the old code:
            backgroundColor: [
                "#3b82f6", "#ec4899", "#f97316", "#14B8A6", "#8b5cf6",
                "#f59e0b", "#F43F5E", "#6EE7B7", "#C084FC", "#22D3EE"
            ]
        }]
    });

    const createProjectChartData = () => {
        const founderNames = ws.part1Answers.founderNames || [];
        const breakdown = {};

        reservedPools.forEach((rp) => {
            breakdown[rp.name] = rp.weight || 0;
        });

        let totalFinancial = 0;
        let totalTime = 0;
        let totalAssets = 0;
        founderNames.forEach((fn) => {
            const core = coreBreakdown[fn] || {};
            totalFinancial += core.financial || 0;
            totalTime += core.time || 0;
            totalAssets += core.assets || 0;
        });
        breakdown["Financial Investment"] = totalFinancial;
        breakdown["Time Commitment"] = totalTime;
        breakdown["Asset Contribution"] = totalAssets;

        // factorAllocations
        Object.keys(factorAllocations[founderNames[0]] || {}).forEach(factorKey => {
            breakdown[factorKey] = founderNames.reduce(
                (sum, fn) => sum + (factorAllocations[fn]?.[factorKey] || 0),
                0
            );
        });

        return {
            labels: Object.keys(breakdown),
            datasets: [{
                data: Object.values(breakdown),
                // ORIGINAL color array from the old code:
                backgroundColor: [
                    "#EC4899", "#F97316", "#10B8A6", "#3B82F6", "#8b5cf6",
                    "#f59e0b", "#F43F5E", "#6EE7B7", "#C084FC", "#22D3EE"
                ]
            }]
        };
    };

    const formatFactorName = (key) => {
        return key.replace(/([A-Z])/g, ' $1').replace(/^\w/, c => c.toUpperCase());
    };

    const getNonZeroFactors = (factors) => {
        return Object.entries(factors)
            .filter(([_, value]) => value > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([key, value]) => ({
                name: formatFactorName(key),
                value: value.toFixed(2)
            }));
    };

    const generateAIReport = async () => {
        setIsGenerating(true);
        try {
            const response = await fetch("https://api.openai.com/v1/chat/completions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
                },
                body: JSON.stringify({
                    model: "gpt-4o-mini",
                    messages: [{
                        role: "user",
                        content: `
Using the following equity breakdown data, generate an AI report that provides a detailed explanation for each founder's equity position. Apply these rules for each founder based on their total equity percentage:

- If the total equity is between 85% and 100%:
  "Oh wow you are really taking the lead on this venture! While you obviously have taken on a significant amount of the work within the company, you may want to consider delegation to your cofounders in order to be able to execute on your idea. Additionally, some cofounders have stated that they do not think that their partner would have 'stuck around' if they didn't have a substantial amount of equity within the company. On the opposite end, some cofounders were grateful that the equity reflected the work/risk/investment provided by their cofounder as it avoided any discouragement or bitterness when one wasn't contributing as much. At this level it also raises the question: do your cofounders have enough equity to be fully committed for the long haul? If their shares are too small, they may view their efforts more like a short-term job than a shared entrepreneurial journey, leading to halfhearted engagement and making it tough for you to rely on them during crunch times or when pivoting strategies. To address this, consider whether you have a structure in place to reward continued contributions—for instance, performance-based or milestone-based vesting, or offering additional grants to cofounders who take on greater responsibilities. A modest increase in cofounder stakes could pay off significantly if it secures their active participation and emotional investment; striking a thoughtful balance ensures everyone feels recognized for their role and stays dedicated to the company’s success."

- If the total equity is between 60% and 85%:
  "Having 60–85% of the company typically places you in a strong majority position. You’ll likely set the overarching vision and hold significant sway over critical decisions. This can be beneficial if you have a clear vision, but ensure other cofounders still feel their voices are heard and their equity reflects their ongoing contributions. In many startups, a single founder might take on 60–85% if they’ve personally financed a big chunk of operations or brought the original idea and the foundational work. Be sure to formalize everyone’s roles to avoid confusion about why you hold the majority stake. If done transparently, this equity division can maintain morale and keep everyone aligned on shared goals."

- If the total equity is between 40% and 60%:
  "If your equity stake sits in the 40–60% range, you likely play a pivotal role in the company while sharing substantial ownership with at least one other cofounder. This balance can be positive because you have significant influence without completely overshadowing your partners. On the other hand, if you’re near the higher end—say 55–60%—you might approach a de facto majority, making it essential to ensure the rest of the team still feels valued and heard. If you’re closer to 40%, you remain a major voice but may need to build consensus more often on key decisions. In either scenario, laying out clear roles, decision-making processes, and pathways for additional equity or responsibility helps maintain a sense of shared commitment. Ultimately, being in this 'middle-majority' zone can foster strong collaboration if everyone understands how contributions are recognized and how critical decisions will be made."

- If the total equity is between 20% and 40%:
  "If you hold 20–40% equity, you’re likely a core member of the founding or early team, yet you’re not the primary decision-maker. This share often reflects a combination of your specialized skill set, partial investment of funds, or a split of responsibilities with a cofounder who holds a majority. That said, the closer you are to the lower end (20–25%), the more you might question the risk-versus-reward balance—especially if you’re putting in long hours and facing the same startup pressures as others. Clarity around your role, future equity opportunities, and performance milestones can help ensure your stake feels proportional to the contributions and risks you’re taking on. Additionally, discuss transparent paths for growth if you sense your equity share might not fully compensate for your efforts. This could mean the potential for additional grants, performance-based increments, or profit-sharing mechanisms. Just as with smaller equity holders, you don’t want to be in a position where the toll of the entrepreneurial journey outweighs the benefits. Regular check-ins with other cofounders to recalibrate roles and responsibilities will help maintain motivation, alignment, and a sense that everyone’s getting their fair share of the upside."

- If the total equity is between 0% and 20%:
  "If you hold somewhere between 0–20% of the company, you’re likely involved in a specific, specialized capacity or you joined after the original idea and groundwork were already established. While even a small slice of a high-growth startup can turn out to be valuable, you may have limited sway over larger strategic decisions or pivots. It’s crucial to clarify how your contributions tie into the broader vision, and whether there’s a path to earn more equity if you assume bigger responsibilities over time. Establishing clear milestones or performance-based triggers can help you feel confident that your stake will grow in line with your contributions. That said, being on the lower end of the equity spectrum can make the entrepreneurial journey feel disproportionately risky. Long hours, emotional ups and downs, and financial uncertainty can weigh heavily when the potential upside seems small. If you’re concerned that the reward doesn’t match the toll of startup life, discuss alternative compensation structures—such as salary bumps, bonuses, or additional equity grants tied to performance. Ultimately, ensuring there’s a fair balance of risk and reward will help keep you motivated and aligned with the company’s success."

- If the equity split is exactly 50/50:
  "A 50/50 split is common among two-person founding teams, symbolizing complete equality in ownership, decision-making, and responsibility. This can work smoothly if you and your partner share similar values, vision, and work ethic, because neither can unilaterally outvote the other. However, a deadlock can occur if you disagree on major decisions—so make sure you have a plan for resolving disputes or stalemates. Many 50/50 partners create a neutral mechanism (like a board member or a trusted advisor) for arbitration. If you both bring equal skills and commitment, this evenly distributed equity can be empowering, fostering a deep sense of mutual ownership and accountability."

Equity Breakdown by Contribution Areas: ${reservedPools.map(rp => `${rp.name}: ${rp.weight.toFixed(2)}%`).join(", ")}

Equity Breakdown by Team Member: ${founderEntries.map(([fn, eq]) => `${fn}: ${eq.toFixed(2)}%`).join(", ")}

Generate the report accordingly for each founder.
    `
                    }]
                })
            });
            const data = await response.json();
            setAiReport(data.choices?.[0]?.message?.content || "No report generated.");
        } catch (error) {
            console.error("AI Report error:", error);
            setAiReport("Error generating report. Please try again.");
        }
        setIsGenerating(false);
    };

    return (
        <div className="wrapper" style={{ color: "#28435a" }}>
            <div
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    zIndex: 9999,
                }}
                id="help-panel"
            >
                {showHelpPanel && (
                    <div
                        style={{
                            backgroundColor: "#FAFAFA",
                            border: "1px solid #ccc",
                            padding: "16px",
                            borderRadius: "6px",
                            marginBottom: "8px",
                            width: "280px",
                            fontSize: "14px",
                            color: "#28435a"
                        }}
                    >
                        <p style={{ marginBottom: "12px", fontWeight: "600" }}>
                            Founder Insight Articles:
                        </p>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: "8px" }}>
                                <a
                                    href="https://thecofoundershub.com/insights/"
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#f7931e", textDecoration: "underline" }}
                                >
                                    📌 Will you VEST? 4 years with 1 year cliff
                                </a>
                            </li>
                            <li style={{marginBottom: "8px"}}>
                                <a href="https://thecofoundershub.com/insights/"
                                   target="_blank"
                                   rel="noreferrer"
                                   style={{color: "#f7931e", textDecoration: "underline"}}>
                                    💡 Is investment a loan to be paid back from Revenue?
                                </a>
                            </li>
                            <li style={{marginBottom: "8px"}}>
                                <a href="https://thecofoundershub.com/insights/"
                                   target="_blank"
                                   rel="noreferrer"
                                   style={{color: "#f7931e", textDecoration: "underline"}}>
                                    🏢 Asset ownership: Company vs Personal
                                </a>
                            </li>
                            <li style={{marginBottom: "8px"}}>
                                <a href="https://thecofoundershub.com/insights/"
                                   target="_blank"
                                   rel="noreferrer"
                                   style={{color: "#f7931e", textDecoration: "underline"}}>
                                    ⚖️ Liability sunset clauses & equity redistribution
                                </a>
                            </li>
                            <li style={{marginBottom: "8px"}}>
                                <a href="https://thecofoundershub.com/insights/"
                                   target="_blank"
                                   rel="noreferrer"
                                   style={{color: "#f7931e", textDecoration: "underline"}}>
                                    📋 Founder expense responsibilities
                                </a>
                            </li>
                            <li>
                                <a href="https://thecofoundershub.com/insights/"
                                   target="_blank"
                                   rel="noreferrer"
                                   style={{color: "#f7931e", textDecoration: "underline"}}>
                                    💭 Final thoughts on equity distribution
                                </a>
                            </li>
                        </ul>
                    </div>
                )}
                <button
                    onClick={() => setShowHelpPanel(!showHelpPanel)}
                    style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#28435a",
                        color: "#fff",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: "18px",
                    }}
                    title="View Founder Insights"
                >
                    ?
                </button>
            </div>

            <div ref={reportRef} style={{ padding: "20px" }}>
                {/* Report Header */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px"
                }}>
                    <h1 style={{ color: "#28435a" }}>Equity Report for {companyName}</h1>
                    <button
                        onClick={exportToPDF}
                        disabled={isExporting}
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#ea7a0a",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "16px",
                            height: "42px"
                        }}
                    >
                        {isExporting ? "Exporting..." : "Export PDF"}
                    </button>
                </div>

                {/* Detailed Founder Breakdown */}
                <div className="section">
                    <h2>Team Member Equity Breakdown</h2>
                    {founderEntries.map(([founder, total]) => {
                        const core = coreBreakdown[founder] || {};
                        const factors = factorAllocations[founder] || {};
                        const nonZeroFactors = getNonZeroFactors(factors);

                        return (
                            <div key={founder} className="founder-breakdown">
                                <h3>{founder}</h3>
                                <table className="equity-details">
                                    <tbody>
                                    {core.financial > 0 && (
                                        <tr>
                                            <td>Financial Investment</td>
                                            <td>{core.financial.toFixed(2)}%</td>
                                        </tr>
                                    )}
                                    {core.time > 0 && (
                                        <tr>
                                            <td>Time Commitment</td>
                                            <td>{core.time.toFixed(2)}%</td>
                                        </tr>
                                    )}
                                    {core.assets > 0 && (
                                        <tr>
                                            <td>Asset Contribution</td>
                                            <td>{core.assets.toFixed(2)}%</td>
                                        </tr>
                                    )}

                                    {nonZeroFactors.map((factor, index) => (
                                        <tr key={`factor-${index}`}>
                                            <td>{factor.name}</td>
                                            <td>{factor.value}%</td>
                                        </tr>
                                    ))}

                                    <tr className="total-row">
                                        <td>Total Equity</td>
                                        <td>{total.toFixed(2)}%</td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>

                {/* Reserved Pools Table */}
                <div className="section">
                    <h2>Reserved Equity Pools</h2>
                    <table className="equity-summary">
                        <thead>
                        <tr>
                            <th>Pool</th>
                            <th>Weight (%)</th>
                        </tr>
                        </thead>
                        <tbody>
                        {reservedPools.map((p, i) => (
                            <tr key={i}>
                                <td>{p.name}</td>
                                <td>{p.weight.toFixed(2)}%</td>
                            </tr>
                        ))}
                        <tr className="total-row">
                            <td>Total Reserved</td>
                            <td>{reservedEquityTotal.toFixed(2)}%</td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                {/* Pie Charts */}
                <div className="section" style={{ marginTop: "24px", display: "flex", gap: "24px" }}>
                    <div style={{ flex: 1 }}>
                        <h2>Breakdown by Project</h2>
                        <div style={{ height: "300px", maxWidth: "600px" }}>
                            <Pie data={createProjectChartData()} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h2>Breakdown by Team Member</h2>
                        <div style={{ height: "300px", maxWidth: "600px" }}>
                            <Pie data={createChartData(founderEntries, reservedPools)} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>
                </div>

                {/* AI Report Section */}
                <div style={{ marginTop: "24px", textAlign: "center" }}>
                    <button
                        onClick={generateAIReport}
                        style={{
                            padding: "10px 20px",
                            backgroundColor: "#f7931e",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            marginBottom: "16px",
                            cursor: "pointer",
                            fontSize: "16px",
                        }}
                        disabled={isGenerating}
                    >
                        {isGenerating ? "Generating..." : "Generate AI Analysis"}
                    </button>
                    {aiReport && (
                        <div
                            style={{
                                marginTop: "16px",
                                textAlign: "left",
                                whiteSpace: "pre-wrap",
                                background: "#f9f9f9",
                                padding: "16px",
                                borderRadius: "8px",
                                marginBottom: "16px",
                                color: "#28435a"
                            }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                children={aiReport}
                                components={{
                                    p: ({ node, ...props }) => <p {...props} style={{ marginBottom: "8px" }} />,
                                    h2: ({ node, ...props }) => <h2 {...props} style={{ margin: "8px 0" }} />,
                                    h3: ({ node, ...props }) => <h3 {...props} style={{ margin: "6px 0" }} />,
                                    li: ({ node, ...props }) => <li {...props} style={{ marginBottom: "4px" }} />,
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="section">
                    <h2>Executive Summary</h2>
                    <textarea
                        placeholder="Is there any notes/comment you want added to the report?"
                        value={ws.part1Answers?.reportNotes || ''}
                        onChange={(e) => {
                            const currentPart1 = ws.part1Answers || {};
                            const updatedPart1 = { ...currentPart1, reportNotes: e.target.value };
                            updateWorkspaceData(ws.id, { ...ws, part1Answers: updatedPart1 });
                        }}
                        style={{
                            width: "100%",
                            minHeight: "100px",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            marginBottom: "16px",
                            fontFamily: "inherit",
                            fontSize: "14px",
                            color: "#28435a"
                        }}
                    />
                </div>

                <div className="section" style={{marginTop: "40px", pageBreakBefore: 'always'}}>
                    <h2>Founder Signatures</h2>
                    <p style={{color: '#28435a', marginBottom: '24px'}}>
                        By signing below, we acknowledge our agreement to the equity distribution outlined in this
                        report.
                    </p>

                    {/* Add total validation */}
                    <div style={{
                        marginBottom: '24px',
                        color: Math.abs(totalAllocated - 100) > 0.01 ? '#dc2626' : '#16a34a',
                        fontWeight: 500
                    }}>
                        Total Allocated Equity: {totalAllocated.toFixed(2)}%
                        {Math.abs(totalAllocated - 100) > 0.01 && (
                            <span style={{marginLeft: '8px'}}>⚠️ Total must equal 100%</span>
                        )}
                    </div>


                    {(ws.part1Answers?.founderNames || []).map(founder => {
                        const calculatedEquity = (finalEquityMap[founder] || 0).toFixed(2);
                        const userEquity = ws.part2Answers?.finalDecidedEquity?.[founder];
                        const displayValue = rawInputs[founder] ?? (userEquity ? userEquity.toFixed(2) : calculatedEquity);

                        return (
                            <div key={founder} style={{margin: '30px 0', color: "#28435a"}}>
                                <div style={{marginBottom: '12px'}}>
                                    <label>Final Decided Equity for {founder}:</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={displayValue}
                                        onChange={(e) => {
                                            const rawValue = e.target.value;
                                            // Allow full editing including partial numbers
                                            if (/^(\d*)([.]\d{0,2})?$/.test(rawValue)) {
                                                setRawInputs(prev => ({
                                                    ...prev,
                                                    [founder]: rawValue
                                                }));
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = parseFloat(e.target.value);
                                            if (!isNaN(value) && value >= 0 && value <= 100) {
                                                const formatted = Number(value.toFixed(2));
                                                const finalDecidedEquity = {
                                                    ...(ws.part2Answers?.finalDecidedEquity || {}),
                                                    [founder]: formatted
                                                };
                                                updateWorkspaceData(ws.id, {
                                                    ...ws,
                                                    part2Answers: {
                                                        ...ws.part2Answers,
                                                        finalDecidedEquity
                                                    }
                                                });
                                            }
                                            setRawInputs(prev => ({
                                                ...prev,
                                                [founder]: undefined
                                            }));
                                        }}
                                        onFocus={() => {
                                            const currentValue = userEquity?.toFixed(2) || calculatedEquity;
                                            setRawInputs(prev => ({
                                                ...prev,
                                                [founder]: currentValue
                                            }));
                                        }}
                                        style={{
                                            width: '80px',
                                            padding: '4px 8px',
                                            border: '1px solid #cbd5e1',
                                            borderRadius: '4px',
                                            marginRight: '8px'
                                        }}
                                    />%
                                    <span style={{color: '#64748b', fontSize: '0.875rem'}}>
                    (Calculated: {calculatedEquity}%)
                </span>
                                </div>

                                {/* Existing signature lines */}
                                <div style={{
                                    width: '300px',
                                    height: '24px',
                                    marginBottom: '4px'
                                }}></div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '400px',
                                    fontSize: '14px'
                                }}>
                                    <span>{founder}: _________________</span>
                                    <span>Date: _________________</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default ReportPage;
